/**
 * SystemBenchmark — Übersichtliche Noble-Slate Executive Matrix (Soll / Ist).
 * Konsumiert AppStore + Calculations + echte aktive Salon-Jahresakten ausschließlich lesend.
 * DATENSCHUTZ: Einzelwerte anderer Salons sind STRIKT NUR im God-Mode sichtbar!
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  Activity,
  BarChart3,
  CheckCircle2,
  AlertTriangle,
  XCircle,
  MinusCircle,
  Users,
  TrendingUp,
  Layers,
  Gauge,
  RefreshCw,
  Eye,
  ChevronDown,
  ChevronUp,
  Target,
  SlidersHorizontal,
  Calendar,
} from 'lucide-react';
import useAppStore from '../store/index';
import useAdminStore from '../store/adminStore';
import { useCalculationsSelector } from '../store/selectors/calculations';
import useSystemBenchmarkStore, {
  TEAM_PEER_MIN_STRICT,
  LAND_LABEL,
  resolveActiveBenchmarkYear,
  type BenchmarkDataPoint,
  type BenchmarkDynamicTone,
  type BenchmarkStatus,
  type ExtractedSalonMetrics,
} from '../store/systemBenchmarkStore';

const GOLD = '#dcb68a';

const formatValue = (
  value: number | null | undefined,
  unit: BenchmarkDataPoint['unit']
): string => {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return '—';
  }
  const nf = new Intl.NumberFormat('de-DE', {
    maximumFractionDigits: unit === 'Faktor' ? 2 : unit === '%' ? 1 : 0,
    minimumFractionDigits: unit === 'Faktor' ? 2 : unit === '%' ? 1 : 0,
  });
  const core = nf.format(value);
  if (unit === '%') return `${core} %`;
  if (unit === 'Faktor') return `${core}`;
  if (unit === '€/Std.') return `${core} €/Std.`;
  if (unit === '€/Monat') return `${core} €/Mon.`;
  if (unit === '€') return `${core} €`;
  return core;
};

const formatEuroRange = (min: number, max: number): string => {
  const nf = new Intl.NumberFormat('de-DE', { maximumFractionDigits: 0 });
  return `${nf.format(min)} € – ${nf.format(max)} €`;
};

const getStatusBadge = (
  s: BenchmarkStatus,
  tone?: BenchmarkDynamicTone,
  customLabel?: string
) => {
  const resolveIcon = () => {
    if (s === 'optimal') return CheckCircle2;
    if (s === 'warning') return AlertTriangle;
    if (s === 'critical') return XCircle;
    return MinusCircle;
  };
  const Icon = resolveIcon();

  switch (tone) {
    case 'purple':
      return {
        label: customLabel || 'Exzellent',
        textClass: 'text-purple-300',
        borderClass: 'border-purple-500/50',
        bgClass: 'bg-purple-950/35 shadow-[0_0_12px_rgba(168,85,247,0.28)]',
        valueBoxClass:
          'text-purple-300 border-purple-500/45 bg-purple-950/30 shadow-[0_0_12px_rgba(168,85,247,0.2)]',
        Icon,
      };
    case 'emerald':
      return {
        label: customLabel || 'Gut',
        textClass: 'text-emerald-400',
        borderClass: 'border-emerald-500/45',
        bgClass: 'bg-emerald-950/35',
        valueBoxClass: 'text-emerald-300 border-emerald-500/40 bg-emerald-950/25',
        Icon,
      };
    case 'lime':
      return {
        label: customLabel || 'Solide',
        textClass: 'text-lime-400',
        borderClass: 'border-lime-500/45',
        bgClass: 'bg-lime-950/30',
        valueBoxClass: 'text-lime-300 border-lime-500/40 bg-lime-950/25',
        Icon,
      };
    case 'amber':
      return {
        label: customLabel || 'Grenzwertig',
        textClass: 'text-amber-300',
        borderClass: 'border-amber-500/45',
        bgClass: 'bg-amber-950/35',
        valueBoxClass: 'text-amber-300 border-amber-500/45 bg-amber-950/25',
        Icon,
      };
    case 'rose':
      return {
        label: customLabel || 'Kritisch',
        textClass: 'text-rose-400',
        borderClass: 'border-rose-500/50',
        bgClass: 'bg-rose-950/35',
        valueBoxClass: 'text-rose-300 border-rose-500/45 bg-rose-950/25',
        Icon,
      };
    case 'gold':
      return {
        label: customLabel || 'Preisabhängig',
        textClass: 'text-[#dcb68a]',
        borderClass: 'border-[#dcb68a]/45',
        bgClass: 'bg-[#dcb68a]/12',
        valueBoxClass: 'text-[#dcb68a] border-[#dcb68a]/40 bg-[#dcb68a]/10',
        Icon,
      };
    default:
      break;
  }

  if (s === 'optimal') {
    return {
      label: customLabel || 'Optimal',
      textClass: 'text-emerald-400',
      borderClass: 'border-emerald-500/40',
      bgClass: 'bg-emerald-500/12',
      valueBoxClass: 'text-emerald-300 border-emerald-500/40 bg-emerald-950/25',
      Icon: CheckCircle2,
    };
  }
  if (s === 'warning') {
    return {
      label: customLabel || 'Grenzwertig',
      textClass: 'text-amber-300',
      borderClass: 'border-amber-500/40',
      bgClass: 'bg-amber-500/12',
      valueBoxClass: 'text-amber-300 border-amber-500/45 bg-amber-950/25',
      Icon: AlertTriangle,
    };
  }
  if (s === 'critical') {
    return {
      label: customLabel || 'Kritisch',
      textClass: 'text-rose-400',
      borderClass: 'border-rose-500/45',
      bgClass: 'bg-rose-500/12',
      valueBoxClass: 'text-rose-300 border-rose-500/45 bg-rose-950/25',
      Icon: XCircle,
    };
  }
  return {
    label: customLabel || 'Referenz',
    textClass: 'text-slate-300',
    borderClass: 'border-slate-700',
    bgClass: 'bg-slate-800/60',
    valueBoxClass: 'text-[#dcb68a] border-[#dcb68a]/35 bg-[#dcb68a]/10',
    Icon: MinusCircle,
  };
};

const getTargetHintClass = (
  tone?: BenchmarkDataPoint['targetHintTone']
): string => {
  switch (tone) {
    case 'orange':
      // 1:1 BWA-Planziel-Farbe aus KostenstrukturPage (orange)
      return 'text-orange-400 border-orange-500/45 bg-orange-950/35';
    case 'cyan':
      // Gemeinkosten-Akzent (cyan)
      return 'text-cyan-300 border-cyan-500/45 bg-cyan-950/35';
    case 'teal':
      // Material-Akzent (teal)
      return 'text-teal-300 border-teal-500/45 bg-teal-950/35';
    case 'indigo':
      // Produktive Löhne (indigo)
      return 'text-indigo-300 border-indigo-500/45 bg-indigo-950/35';
    case 'purple':
      // Rentabilitäts-Ziel (> 35 %)
      return 'text-purple-300 border-purple-500/45 bg-purple-950/35';
    default:
      return 'text-[#dcb68a] border-[#dcb68a]/40 bg-[#dcb68a]/12';
  }
};

const getRowAccentClass = (tone?: BenchmarkDynamicTone): string => {
  switch (tone) {
    case 'cyan':
      return 'border-l-4 border-l-cyan-500';
    case 'teal':
      return 'border-l-4 border-l-teal-500';
    case 'indigo':
      return 'border-l-4 border-l-indigo-500';
    case 'orange':
      return 'border-l-4 border-l-orange-500';
    case 'purple':
      return 'border-l-4 border-l-purple-500';
    case 'gold':
      return 'border-l-4 border-l-[#dcb68a]';
    default:
      return '';
  }
};

/**
 * GOD-MODE ONLY: Aufklappbare Prüftabelle aller verglichenen Salons desselben Geschäftsjahres.
 * Normale Mandanten sehen diese Komponente NIEMALS (strikter Datenschutz!).
 */
const ComparedSalonsAuditDrawer: React.FC<{
  salons: ExtractedSalonMetrics[];
  mode: 'solo' | 'team';
  benchmarkYear: string;
}> = ({ salons, mode, benchmarkYear }) => {
  const [open, setOpen] = useState(false);

  if (!salons || salons.length === 0) return null;

  return (
    <div className="mb-6 rounded-xl bg-slate-900/90 border border-amber-500/30 overflow-hidden shadow-lg">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full px-5 py-3 flex items-center justify-between text-left hover:bg-slate-800/50 transition-colors"
      >
        <div className="flex items-center gap-2.5">
          <Eye className="w-4 h-4" style={{ color: GOLD }} />
          <span className="text-xs font-extrabold uppercase tracking-wider text-slate-200">
            🔒 GOD-MODE DIAGNOSE: Vergleichs-Salons im Jahr {benchmarkYear} ({salons.length} aktive {mode === 'solo' ? 'Solo' : 'Team'}-Salons, ohne Archiv)
          </span>
        </div>
        <span
          className="inline-flex items-center gap-1 text-xs font-bold"
          style={{ color: GOLD }}
        >
          {open ? (
            <>
              Ausblenden <ChevronUp className="w-4 h-4" />
            </>
          ) : (
            <>
              Einzelwerte prüfen <ChevronDown className="w-4 h-4" />
            </>
          )}
        </span>
      </button>

      {open && (
        <div className="p-4 border-t border-slate-800 bg-slate-950/80 overflow-x-auto">
          <table className="w-full text-xs font-mono border-collapse">
            <thead>
              <tr
                className="border-b border-slate-800 text-[10px] uppercase tracking-wider text-left"
                style={{ color: GOLD }}
              >
                <th className="py-2 px-2 font-sans">Salon / Mandant ({benchmarkYear})</th>
                <th className="py-2 px-2 text-right">Umsatz Netto/Mon.</th>
                <th className="py-2 px-2 text-right">GK %</th>
                <th className="py-2 px-2 text-right">Lohn %</th>
                <th className="py-2 px-2 text-right">LF aktiv</th>
                <th className="py-2 px-2 text-right">Rentab.</th>
                <th className="py-2 px-2 text-right">Satz o.M. (Empf./Reell)</th>
                <th className="py-2 px-2 text-right">Satz m.M. (Empf./Reell)</th>
                <th className="py-2 px-2 text-right">Soll/Std. (100% / 75%)</th>
                <th className="py-2 px-2 text-right">IST Überschuss</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-800/70">
              {salons.map((s, idx) => (
                <tr
                  key={s.uid || s.mandantNr || idx}
                  className={`hover:bg-slate-900/70 ${!s.isPlausibleForMarket ? 'opacity-50' : ''}`}
                >
                  <td className="py-2 px-2 font-sans font-semibold text-slate-200">
                    <div className="flex items-center gap-1.5">
                      <span>
                        {s.firmenname || 'Unbenannter Salon'}
                        {s.land ? (
                          <span className="ml-2 text-[10px] font-mono text-slate-500">
                            [{s.land}]
                          </span>
                        ) : null}
                      </span>
                      {!s.isPlausibleForMarket && (
                        <span className="text-[9px] px-1.5 py-0.5 rounded bg-rose-500/20 text-rose-300 border border-rose-500/30">
                          unvollständig (ausgefiltert)
                        </span>
                      )}
                    </div>
                    {s.mandantNr && (
                      <div className="text-[10px] text-slate-500 font-mono">
                        {s.mandantNr}
                      </div>
                    )}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-200">
                    {formatValue(s.monatsumsatzNetto, '€')}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {formatValue(s.values.gemeinkostenfaktor, '%')}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {formatValue(s.values.prodLohnkostenfaktor, '%')}
                  </td>
                  <td className="py-2 px-2 text-right font-bold" style={{ color: GOLD }}>
                    {formatValue(s.values.lohnfaktorAktiv, 'Faktor')}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {formatValue(s.values.rentabilitaetSoll, '%')}
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {Math.round(s.values.stundensatzOhneMatEmpfohlen || 0)} /{' '}
                    <strong className="text-white">
                      {Math.round(s.values.stundensatzOhneMatReell || 0)} €
                    </strong>
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {Math.round(s.values.stundensatzInklMatEmpfohlen || 0)} /{' '}
                    <strong className="text-white">
                      {Math.round(s.values.stundensatzInklMatReell || 0)} €
                    </strong>
                  </td>
                  <td className="py-2 px-2 text-right text-slate-300">
                    {Math.round(s.values.sollUmsatz100 || 0)} /{' '}
                    {Math.round(s.values.sollUmsatz75 || 0)} €
                  </td>
                  <td className="py-2 px-2 text-right text-emerald-400 font-bold">
                    {formatValue(s.values.bwaErgebnisProzent, '%')}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

const MatrixSectionCard: React.FC<{
  points: BenchmarkDataPoint[];
  section: BenchmarkDataPoint['section'];
  title: string;
  subtitle: string;
  icon: React.ReactNode;
  mode: 'solo' | 'team';
  peerCount: number;
  marketCount: number;
  showSimilarColumn: boolean;
  benchmarkYear: string;
}> = ({
  points,
  section,
  title,
  subtitle,
  icon,
  mode,
  peerCount,
  marketCount,
  showSimilarColumn,
  benchmarkYear,
}) => {
  const rows = points.filter((p) => p.section === section);
  if (rows.length === 0) return null;

  const renderPeerCol = mode === 'solo' || showSimilarColumn;

  return (
    <section className="mb-7 last:mb-0 rounded-xl bg-slate-900/90 border border-slate-800 shadow-xl overflow-hidden">
      {/* Section Header */}
      <div className="px-5 py-4 bg-slate-950/80 border-b border-slate-800 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
        <div className="flex items-center gap-3">
          <div
            className="w-8 h-8 rounded-lg flex items-center justify-center border shrink-0"
            style={{
              borderColor: 'rgba(220,182,138,0.4)',
              background: 'rgba(220,182,138,0.1)',
              color: GOLD,
            }}
          >
            {icon}
          </div>
          <div>
            <h2 className="text-sm sm:text-base font-extrabold uppercase tracking-wider text-white">
              {title}
            </h2>
            <p className="text-[11px] text-slate-400">{subtitle}</p>
          </div>
        </div>
      </div>

      {/* Spaltenköpfe (Desktop) */}
      <div
        className="hidden md:grid grid-cols-12 items-center px-5 py-3 text-xs font-extrabold uppercase tracking-widest border-b border-slate-800/90 bg-slate-900/60"
        style={{ color: GOLD }}
      >
        <div className={renderPeerCol ? 'col-span-5' : 'col-span-5'}>
          Kennzahl &amp; Planziel
        </div>
        <div className="col-span-2 text-right">Mein Salon ({benchmarkYear})</div>
        {renderPeerCol && (
          <div className="col-span-2 text-right">
            {mode === 'solo'
              ? `Andere Solos ${benchmarkYear} (n=${peerCount})`
              : `Ähnliche ±20% (n=${peerCount})`}
          </div>
        )}
        <div className="col-span-2 text-right">
          Gesamtmarkt {benchmarkYear} ({marketCount} aktiv)
        </div>
        <div className={renderPeerCol ? 'col-span-1 text-right' : 'col-span-3 text-right'}>
          Bewertung
        </div>
      </div>

      {/* Zeilen */}
      <div className="divide-y divide-slate-800/80">
        {rows.map((point) => {
          const st = getStatusBadge(
            point.status,
            point.statusTone,
            point.statusLabel
          );
          const StatusIcon = st.Icon;
          const peerUnlocked = point.peerAvailable === true;
          const sec = point.secondaryMarketNote;
          const rowAccent = getRowAccentClass(
            point.rowAccentTone || point.statusTone
          );
          const isOrangeBwaLabel = point.rowAccentTone === 'orange';
          const isTealMaterialLabel = point.rowAccentTone === 'teal';

          return (
            <div
              key={point.metricKey}
              className={`grid grid-cols-1 md:grid-cols-12 items-center gap-2.5 md:gap-0 px-5 py-4 hover:bg-slate-800/35 transition-colors ${rowAccent}`}
            >
              {/* Kennzahl + Sublabel + BWA-Planziel */}
              <div className="md:col-span-5 pr-3">
                <div className="flex flex-wrap items-center gap-2.5">
                  <span
                    className={`text-base font-bold leading-snug ${
                      isOrangeBwaLabel
                        ? 'text-orange-400'
                        : isTealMaterialLabel
                          ? 'text-emerald-400'
                          : 'text-slate-100'
                    }`}
                  >
                    {point.label}
                  </span>
                  {point.targetHint && (
                    <span
                      className={`text-xs sm:text-[13px] font-mono font-extrabold px-2.5 py-1 rounded-md border ${getTargetHintClass(
                        point.targetHintTone
                      )}`}
                    >
                      {point.targetHint}
                    </span>
                  )}
                </div>
                {point.sublabel && (
                  <div className="text-xs sm:text-[13px] text-slate-300 mt-1 leading-relaxed">
                    {point.sublabel}
                  </div>
                )}
                {point.statusMessage && (
                  <div
                    className={`text-xs font-medium mt-1.5 leading-snug ${st.textClass}`}
                  >
                    {point.statusMessage}
                  </div>
                )}
              </div>

              {/* Spalte 1: Mein Salon */}
              <div className="md:col-span-2 flex md:flex-col items-center md:items-end justify-between md:justify-center">
                <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 font-bold">
                  Mein Salon:
                </span>
                <span
                  className={`inline-block font-mono text-base sm:text-lg font-black px-3 py-1 rounded-lg border ${st.valueBoxClass}`}
                >
                  {formatValue(point.mySalonValue, point.unit)}
                </span>
              </div>

              {/* Spalte 2 (optional zuschaltbar): Ähnliche Salons (±20 %) bzw. Andere Solos */}
              {renderPeerCol && (
                <div className="md:col-span-2 flex md:flex-col items-center md:items-end justify-between md:justify-center">
                  <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 font-bold">
                    {mode === 'solo' ? 'Andere Solos:' : 'Ähnliche (±20%):'}
                  </span>
                  {peerUnlocked ? (
                    <div className="text-right">
                      <span className="font-mono text-base sm:text-lg font-bold text-slate-200">
                        {formatValue(point.peerGroupValue, point.unit)}
                      </span>
                      {sec &&
                        sec.peerValue !== null &&
                        Math.round(sec.peerValue) !==
                          Math.round(point.peerGroupValue || 0) && (
                          <div className="text-xs font-mono text-slate-400 mt-0.5">
                            {sec.label}: {formatValue(sec.peerValue, point.unit)}
                          </div>
                        )}
                    </div>
                  ) : (
                    <span className="inline-block text-xs font-mono text-slate-500 px-2.5 py-1 rounded bg-slate-950/70 border border-slate-800">
                      n ≤ {TEAM_PEER_MIN_STRICT}
                    </span>
                  )}
                </div>
              )}

              {/* Spalte 3: Gesamtmarkt */}
              <div className="md:col-span-2 flex md:flex-col items-center md:items-end justify-between md:justify-center">
                <span className="md:hidden text-xs uppercase tracking-wider text-slate-400 font-bold">
                  Gesamtmarkt:
                </span>
                <div className="text-right">
                  <span className="font-mono text-base sm:text-lg font-bold text-slate-200">
                    {formatValue(point.totalMarketValue, point.unit)}
                  </span>
                  {sec &&
                    sec.marketValue !== null &&
                    Math.round(sec.marketValue) !==
                      Math.round(point.totalMarketValue || 0) && (
                      <div className="text-xs font-mono text-slate-400 mt-0.5">
                        {sec.label}: {formatValue(sec.marketValue, point.unit)}
                      </div>
                    )}
                </div>
              </div>

              {/* Status */}
              <div
                className={`${
                  renderPeerCol ? 'md:col-span-1' : 'md:col-span-3'
                } flex items-center justify-end`}
              >
                <span
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-extrabold uppercase tracking-wider border ${st.textClass} ${st.borderClass} ${st.bgClass}`}
                >
                  <StatusIcon className="w-3.5 h-3.5 shrink-0" />
                  <span>{st.label}</span>
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
};

const SystemBenchmarkView: React.FC = () => {
  const calc = useCalculationsSelector();
  const clientData = useAppStore((s) => s.clientData);
  const settings = useAppStore((s) => s.settings);

  // STRIKT DATENSCHUTZ: Nur im God-Mode darf die Prüftabelle gerendert werden!
  const impersonatedClientId = useAdminStore((s) => s.impersonatedClientId);
  const isGodMode =
    Boolean(impersonatedClientId) ||
    Boolean((clientData as any)?._isGodMode) ||
    (typeof window !== 'undefined' &&
      Boolean(sessionStorage.getItem('GOD_MODE_ID')));

  const {
    benchmarkYear,
    activeTab,
    mode,
    selectedCohortRange,
    pointsSoll,
    pointsIst,
    peerCount,
    marketCount,
    peerAutoUnlocked,
    showSimilarColumn,
    allowCrossCountryComparison,
    ownLand,
    isLoadingCohort,
    hydratedSalons,
    comparedSalons,
    setActiveTab,
    setShowSimilarColumn,
    setAllowCrossCountryComparison,
    loadActiveSalonsFromFirestore,
    recomputeFromLive,
  } = useSystemBenchmarkStore();

  const currentYear = resolveActiveBenchmarkYear(clientData);

  // CSS-zoom in App.tsx bricht position:sticky in Chromium → Sentinel + fixed Portal außerhalb Zoom
  const tabBarSentinelRef = useRef<HTMLDivElement>(null);
  const tabBarInFlowRef = useRef<HTMLDivElement>(null);
  const [isTabBarPinned, setIsTabBarPinned] = useState(false);
  const [tabBarPinTop, setTabBarPinTop] = useState(8);
  const [tabBarHeight, setTabBarHeight] = useState(0);

  const measureTabBarPinTop = useCallback(() => {
    const stickies = Array.from(
      document.querySelectorAll<HTMLElement>('.sticky.z-40')
    );
    let bottom = 0;
    for (const el of stickies) {
      const style = window.getComputedStyle(el);
      if (style.display === 'none' || style.visibility === 'hidden') continue;
      const rect = el.getBoundingClientRect();
      if (rect.height < 8) continue;
      // App-Header klebt am oberen Rand
      if (rect.top > -4 && rect.top < 48) {
        bottom = Math.max(bottom, rect.bottom);
      }
    }
    const fallback = window.matchMedia('(min-width: 768px)').matches ? 152 : 12;
    setTabBarPinTop(bottom > 0 ? Math.round(bottom + 8) : fallback);
  }, []);

  useEffect(() => {
    measureTabBarPinTop();
    window.addEventListener('resize', measureTabBarPinTop);
    return () => window.removeEventListener('resize', measureTabBarPinTop);
  }, [measureTabBarPinTop]);

  useEffect(() => {
    const sentinel = tabBarSentinelRef.current;
    if (!sentinel) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const pinned = !entry.isIntersecting;
        setIsTabBarPinned(pinned);
        if (tabBarInFlowRef.current) {
          setTabBarHeight(tabBarInFlowRef.current.offsetHeight);
        }
        measureTabBarPinTop();
      },
      {
        root: null,
        threshold: 0,
        rootMargin: `-${Math.max(tabBarPinTop, 1)}px 0px 0px 0px`,
      }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [tabBarPinTop, measureTabBarPinTop]);

  // Lädt die aktiven Salons exakt für das aktuell gewählte Geschäftsjahr (z. B. 2026 vs. 2026)
  useEffect(() => {
    void loadActiveSalonsFromFirestore(calc, clientData, settings);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentYear]);

  useEffect(() => {
    recomputeFromLive(calc, clientData, hydratedSalons, settings);
  }, [
    calc,
    clientData,
    hydratedSalons,
    settings,
    recomputeFromLive,
    allowCrossCountryComparison,
  ]);

  // Unter der Peer-Schwelle keine Vergleichsspalte — auch nicht manuell zuschaltbar
  useEffect(() => {
    if (!peerAutoUnlocked && showSimilarColumn) {
      setShowSimilarColumn(false);
    }
  }, [peerAutoUnlocked, showSimilarColumn, setShowSimilarColumn]);

  const points = activeTab === 'soll' ? pointsSoll : pointsIst;
  const similarToggleEnabled = mode === 'team' && peerAutoUnlocked;
  const similarToggleTitle = similarToggleEnabled
    ? `Umsatzkorridor ±20 %: ${formatEuroRange(
        selectedCohortRange.min,
        selectedCohortRange.max
      )} Netto/Monat — ${peerCount} vergleichbare Salons`
    : `Vergleich erst ab mehr als ${TEAM_PEER_MIN_STRICT} Salons im ±20 %-Fenster (aktuell n=${peerCount})`;

  const tabBarInner = (
    <>
      <div className="inline-flex p-1 rounded-xl bg-slate-900 border border-slate-800 gap-1">
        {(
          [
            {
              id: 'soll' as const,
              label: 'Kalkulatorische Werte (Soll)',
            },
            {
              id: 'ist' as const,
              label: 'Laufende Performance (Ist)',
            },
          ] as const
        ).map((tab) => {
          const active = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className="px-4 py-2 rounded-lg text-xs font-extrabold uppercase tracking-wider transition-all"
              style={{
                background: active
                  ? 'linear-gradient(135deg, rgba(220,182,138,0.24) 0%, rgba(220,182,138,0.09) 100%)'
                  : 'transparent',
                color: active ? GOLD : '#94a3b8',
                border: active
                  ? `1px solid ${GOLD}`
                  : '1px solid transparent',
              }}
            >
              {tab.label}
            </button>
          );
        })}
      </div>

      {mode === 'team' && (
        <button
          type="button"
          role="switch"
          aria-checked={showSimilarColumn}
          aria-disabled={!similarToggleEnabled}
          disabled={!similarToggleEnabled}
          onClick={() => {
            if (!similarToggleEnabled) return;
            setShowSimilarColumn(!showSimilarColumn);
          }}
          title={similarToggleTitle}
          className={`inline-flex items-center gap-3 pl-3 pr-2 py-1.5 rounded-xl border text-left transition-all ${
            similarToggleEnabled
              ? 'cursor-pointer hover:border-[#dcb68a]/70 hover:bg-[#dcb68a]/10'
              : 'cursor-not-allowed opacity-55'
          }`}
          style={{
            color: showSimilarColumn && similarToggleEnabled ? GOLD : '#94a3b8',
            borderColor:
              showSimilarColumn && similarToggleEnabled
                ? 'rgba(220,182,138,0.65)'
                : similarToggleEnabled
                  ? 'rgba(220,182,138,0.35)'
                  : '#334155',
            background:
              showSimilarColumn && similarToggleEnabled
                ? 'rgba(220,182,138,0.16)'
                : 'rgba(15,23,42,0.9)',
          }}
        >
          <span className="flex flex-col min-w-0">
            <span className="inline-flex items-center gap-1.5 text-[10px] font-extrabold uppercase tracking-wider">
              <SlidersHorizontal className="w-3.5 h-3.5 shrink-0" />
              Ähnliche (±20 %)
            </span>
            <span className="text-[10px] font-mono text-slate-400 normal-case tracking-normal">
              {similarToggleEnabled
                ? showSimilarColumn
                  ? `Spalte aktiv · n=${peerCount}`
                  : `Zum Anzeigen tippen · n=${peerCount}`
                : `Gesperrt · n=${peerCount} (min. >${TEAM_PEER_MIN_STRICT})`}
            </span>
          </span>
          <span
            aria-hidden
            className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors ${
              showSimilarColumn && similarToggleEnabled
                ? 'border-[#dcb68a]/80 bg-[#dcb68a]'
                : 'border-slate-600 bg-slate-800'
            }`}
          >
            <span
              className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                showSimilarColumn && similarToggleEnabled
                  ? 'translate-x-6'
                  : 'translate-x-1'
              }`}
            />
          </span>
        </button>
      )}
    </>
  );

  const tabBarClassName =
    'w-full py-2.5 px-3 rounded-xl bg-slate-950/95 backdrop-blur-md border border-slate-800 shadow-[0_10px_25px_rgba(0,0,0,0.65)] flex flex-wrap items-center justify-between gap-3';

  return (
    <div
      className="min-h-[75vh] w-full rounded-2xl border border-slate-800 p-4 sm:p-6 md:p-8 text-slate-100 shadow-2xl"
      style={{
        background:
          'linear-gradient(155deg, #0f141c 0%, #0b0e14 55%, #07090d 100%)',
      }}
    >
      {/* Header */}
      <div className="rounded-xl bg-slate-900/90 border border-slate-800 p-5 mb-5 shadow-lg">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <div className="flex items-center gap-2 mb-1.5">
              <BarChart3 className="w-4 h-4" style={{ color: GOLD }} />
              <span
                className="text-[10px] font-extrabold uppercase tracking-[0.25em]"
                style={{ color: GOLD }}
              >
                Analyse Tools · Geschäftsjahr {benchmarkYear}
              </span>
            </div>
            <h1 className="text-2xl sm:text-3xl font-black tracking-tight text-white uppercase">
              System<span style={{ color: GOLD }}>Benchmark</span>
            </h1>
            <p className="text-xs text-slate-400 mt-1">
              Anonymer Echtzeit-Vergleich aktiver{' '}
              {mode === 'solo' ? 'Solo-Salons' : 'Team-Salons'} in{' '}
              <strong className="text-slate-200">
                {allowCrossCountryComparison
                  ? 'allen Ländern'
                  : LAND_LABEL[ownLand]}
              </strong>{' '}
              · Geschäftsjahr{' '}
              <strong className="text-slate-200">{benchmarkYear}</strong>
              {' '}(archivierte Akten ausgeschlossen).
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            <button
              type="button"
              role="switch"
              aria-checked={allowCrossCountryComparison}
              onClick={() =>
                setAllowCrossCountryComparison(!allowCrossCountryComparison)
              }
              title={
                allowCrossCountryComparison
                  ? 'Länderübergreifender Vergleich aktiv — tippen für nur gleiches Land'
                  : `Standard: nur ${LAND_LABEL[ownLand]} — tippen, um DE/CH/AT/IT zu mischen`
              }
              className="inline-flex items-center gap-2.5 pl-3 pr-2 py-1.5 rounded-xl border text-left transition-all cursor-pointer hover:border-[#dcb68a]/70 hover:bg-[#dcb68a]/10"
              style={{
                color: allowCrossCountryComparison ? GOLD : '#94a3b8',
                borderColor: allowCrossCountryComparison
                  ? 'rgba(220,182,138,0.65)'
                  : 'rgba(51,65,85,0.9)',
                background: allowCrossCountryComparison
                  ? 'rgba(220,182,138,0.16)'
                  : 'rgba(15,23,42,0.9)',
              }}
            >
              <span className="flex flex-col min-w-0">
                <span className="text-[10px] font-extrabold uppercase tracking-wider">
                  {allowCrossCountryComparison
                    ? 'Alle Länder'
                    : `Nur ${LAND_LABEL[ownLand]}`}
                </span>
                <span className="text-[10px] font-mono text-slate-400 normal-case tracking-normal">
                  {allowCrossCountryComparison
                    ? 'Cross-Country · Opt-in'
                    : 'Standard · Landessperre'}
                </span>
              </span>
              <span
                aria-hidden
                className={`relative inline-flex h-7 w-12 shrink-0 items-center rounded-full border transition-colors ${
                  allowCrossCountryComparison
                    ? 'border-[#dcb68a]/80 bg-[#dcb68a]'
                    : 'border-slate-600 bg-slate-800'
                }`}
              >
                <span
                  className={`inline-block h-5 w-5 rounded-full bg-white shadow transition-transform ${
                    allowCrossCountryComparison
                      ? 'translate-x-6'
                      : 'translate-x-1'
                  }`}
                />
              </span>
            </button>
            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold uppercase tracking-wider border"
              style={{
                color: GOLD,
                borderColor: 'rgba(220,182,138,0.5)',
                background: 'rgba(220,182,138,0.12)',
              }}
            >
              <Calendar className="w-3.5 h-3.5" />
              Jahr: {benchmarkYear}
            </span>

            <span
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-extrabold uppercase tracking-wider border"
              style={{
                color: GOLD,
                borderColor: 'rgba(220,182,138,0.5)',
                background: 'rgba(220,182,138,0.12)',
              }}
            >
              <Users className="w-3.5 h-3.5" />
              Modus: {mode === 'solo' ? 'SOLO' : 'TEAM'}
            </span>

            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-mono border border-slate-800 bg-slate-950 text-slate-300">
              {isLoadingCohort ? (
                <>
                  <RefreshCw className="w-3.5 h-3.5 animate-spin text-amber-300" />
                  Lade Jahr {benchmarkYear}…
                </>
              ) : (
                <>
                  Aktive {mode === 'solo' ? 'Solo' : 'Team'}-Salons ({benchmarkYear}):{' '}
                  <strong style={{ color: GOLD }}>{marketCount}</strong>
                </>
              )}
            </span>
          </div>
        </div>
      </div>

      {/* DATENSCHUTZ-SCHRANKE: Prüftabelle der Einzel-Salons NUR im God-Mode! */}
      {isGodMode && (
        <ComparedSalonsAuditDrawer
          salons={comparedSalons}
          mode={mode}
          benchmarkYear={benchmarkYear}
        />
      )}

      {/* Umschalt-Leiste: sticky via fixed Portal (App-CSS-zoom killt position:sticky) */}
      <div ref={tabBarSentinelRef} className="h-px w-full" aria-hidden />
      <div
        className="mb-6"
        style={isTabBarPinned ? { height: tabBarHeight || undefined } : undefined}
      >
        <div
          ref={tabBarInFlowRef}
          className={`${tabBarClassName} ${isTabBarPinned ? 'invisible pointer-events-none' : ''}`}
        >
          {tabBarInner}
        </div>
      </div>
      {isTabBarPinned &&
        createPortal(
          <div
            className="fixed z-[45] pointer-events-none"
            style={{
              top: tabBarPinTop,
              left: 0,
              right: 0,
            }}
          >
            <div className="pointer-events-auto mx-auto w-full max-w-7xl px-4 md:pl-[calc(4rem+1rem)] md:pr-4">
              <div className={tabBarClassName}>{tabBarInner}</div>
            </div>
          </div>,
          document.body
        )}

      {/* Tabellen-Sektionen */}
      {activeTab === 'soll' ? (
        <>
          <MatrixSectionCard
            points={points}
            section="faktoren"
            title="A · Kennzahlen & Faktoren"
            subtitle="Gemeinkostenquote, produktive Lohnquote, aktiver Lohnfaktor und Soll-Rentabilität"
            icon={<Gauge className="w-4 h-4" />}
            mode={mode}
            peerCount={peerCount}
            marketCount={marketCount}
            showSimilarColumn={showSimilarColumn}
            benchmarkYear={benchmarkYear}
          />
          <MatrixSectionCard
            points={points}
            section="kostenquoten"
            title="B · BWA-Kostenquoten (% vom Netto-Umsatz)"
            subtitle="1:1 synchron zu den BWA-Planzielen der Kostenstruktur (Materialeinsatz 8–10 %, Raumkosten 10–12 %, Werbe-/Reisekosten 2–5 %, sonstige Kosten ca. 10–12 %)"
            icon={<Layers className="w-4 h-4" />}
            mode={mode}
            peerCount={peerCount}
            marketCount={marketCount}
            showSimilarColumn={showSimilarColumn}
            benchmarkYear={benchmarkYear}
          />
          <MatrixSectionCard
            points={points}
            section="leistung"
            title="C · Löhne, Stundensätze & Unternehmerlohn (Brutto)"
            subtitle="Vertraglicher Grundlohn vs. Gesamtlohn (inkl. Zusatz/Bonus), Stundensätze und Unternehmerlohn"
            icon={<TrendingUp className="w-4 h-4" />}
            mode={mode}
            peerCount={peerCount}
            marketCount={marketCount}
            showSimilarColumn={showSimilarColumn}
            benchmarkYear={benchmarkYear}
          />
          <MatrixSectionCard
            points={points}
            section="sollUmsatz"
            title="D · Mitarbeiter-Soll-Umsatz pro Stunde (Ø produktive MA)"
            subtitle="Separater Block: Welchen Brutto-Stundenumsatz müssen die produktiven Mitarbeiter im Schnitt erwirtschaften?"
            icon={<Target className="w-4 h-4" />}
            mode={mode}
            peerCount={peerCount}
            marketCount={marketCount}
            showSimilarColumn={showSimilarColumn}
            benchmarkYear={benchmarkYear}
          />
        </>
      ) : (
        <MatrixSectionCard
          points={points}
          section="ist"
          title="Laufende Performance (1:1 synchron zur Gesamtleistungsseite)"
          subtitle="Aktive / Reale Rentabilität, Verkaufsfaktor, erreichte Ø Umsatzauslastung und laufender prozentualer Überschuss über Mindestumsatz"
          icon={<Activity className="w-4 h-4" />}
          mode={mode}
          peerCount={peerCount}
          marketCount={marketCount}
          showSimilarColumn={showSimilarColumn}
          benchmarkYear={benchmarkYear}
        />
      )}
    </div>
  );
};

export default SystemBenchmarkView;
